<script setup lang="ts">
/**
 * IGNITE-104: tokenised affiliate product page, /affiliate/{token}
 *
 * Renders exactly the same product components as /product/{slug} - this is the
 * PDP, only reached through a token instead of a guessable slug. The product is
 * resolved by matching the token against customFields.pim.goCommerce, and is
 * only rendered while its promotional discount is inside its date window.
 * Anything else shows the not-found page, which says nothing about why.
 */
import type { CartItemType, ProductType } from "@/types";
import {
  findProductByAffiliateToken,
  hasValidDiscount,
} from "~/composables/useAffiliateProduct";
import { viewItemEvent, addToCartEvent } from "~/utils/gtmUtils";
import { ArrowPathIcon } from "@heroicons/vue/24/outline";

const commerceStore = useCommerceStore();
const activationStore = useActivationStore();
const mainStore = useMainStore();

const { getProducts } = useBackendService();

const cardType = computed(
  () => mainStore?.globalSettings?.CardType || "Classic"
);

const route = useRoute();
const token = ref<string>(route.params.token?.toString() ?? "");
const product = ref<ProductType>();
/**
 * Three states, not two. Until the catalogue call comes back we know nothing,
 * so the page shows a spinner - rendering the not-found page first and swapping
 * it for the product a moment later reads as a broken link.
 */
const loading = ref(true);
const { totalItemsInCart } = storeToRefs(commerceStore);

const cartItem = ref<CartItemType>({
  product: {} as ProductType,
  quantity: 1,
});

const handlePosition = ref("0px");
const updateHandlePosition = () => {};

const resolveProduct = async () => {
  try {
    const allProducts = await getProducts();

    if (!Array.isArray(allProducts)) {
      console.warn("[Affiliate] Products API returned invalid data");
      return;
    }

    commerceStore.updateProducts(allProducts);

    const match = findProductByAffiliateToken(allProducts, token.value);
    if (!match) {
      console.warn("[Affiliate] No affiliate product for token");
      return;
    }

    if (!hasValidDiscount(match)) {
      console.warn(
        "[Affiliate] Affiliate product found but its discount is not currently running:",
        match.code
      );
      return;
    }

    product.value = match;
    cartItem.value.product = match;

    useHead({ title: `${match.name} | OnePass mobile` });
    viewItemEvent(match, cartItem.value.quantity);
  } catch (error) {
    console.error("[Affiliate] Failed to resolve affiliate product:", error);
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  resolveProduct();
});

// Same signature the product components call with on /product/{slug}
const updateQuantity = (action: string) => {
  if (action === "add") {
    cartItem.value.quantity++;
  } else if (cartItem.value.quantity > 1) {
    cartItem.value.quantity--;
  }
};

const clearFields = () => {
  cartItem.value = {
    product: product.value as ProductType,
    quantity: 1,
  };
};

const addToCart = () => {
  if (!product.value || !cartItem.value.quantity) return;
  commerceStore.addToCart(cartItem.value);
  addToCartEvent(product.value, cartItem.value.quantity);
  clearFields();
};

const buyNow = () => {
  addToCart();
  activationStore.clearActivationSteps();
  navigateTo("/checkout");
};

// The point of the token is that the page is not discoverable, so keep it out
// of search results even if the URL leaks
useHead({
  meta: [{ name: "robots", content: "noindex, nofollow" }],
});
</script>

<template>
  <!--
    Same loading indicator the account page uses. Fixed rather than in flow:
    the page is otherwise empty while resolving, so a block in flow sits at the
    top of the viewport. pointer-events-none keeps the header clickable.
  -->
  <div
    v-if="loading"
    class="fixed inset-0 flex items-center justify-center pointer-events-none"
    role="status"
    aria-live="polite"
  >
    <ArrowPathIcon class="text-primary animate-spin w-32 h-32" />
    <span class="sr-only">Loading</span>
  </div>
  <template v-else-if="product">
    <ProductClassic
      v-if="cardType === 'Classic'"
      :product="product"
      :cartItem="cartItem"
      :handlePosition="handlePosition"
      :updateHandlePosition="updateHandlePosition"
      :totalItemsInCart="totalItemsInCart"
      :updateQuantity="updateQuantity"
      :addToCart="addToCart"
      :buyNow="buyNow"
      :clearFields="clearFields"
    />
    <ProductDetail
      v-else-if="cardType === 'Modern'"
      :product="product"
      :cartItem="cartItem"
      :handlePosition="handlePosition"
      :updateHandlePosition="updateHandlePosition"
      :totalItemsInCart="totalItemsInCart"
      :updateQuantity="updateQuantity"
      :addToCart="addToCart"
      :buyNow="buyNow"
      :clearFields="clearFields"
    />
  </template>
  <ProductNotFound v-else />
</template>
